import React, { useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { Link, useHistory, useParams } from "react-router-dom";
import { Trans } from "react-i18next";
import { ReactComponent as InvalidDocument } from "@/assets/images/invalid_document.svg";

import Button from "@/components/atoms/Buttons/Button";
import Input from "@/components/atoms/Input";
import useSubmitData from "@/hooks/useSubmitData";
import useRedirect from "@/hooks/useRedirect";
import { confirmTransaction, acceptTermsAndConfirm } from "@/services/api";
import i18n from "@/translate/i18n";
import { conditionalUserSchema } from "@/utils/validations";
import { formatDocumentMask } from "@/utils";
import { FakeInput } from "@/components/molecules/Wrappers/FakeInput";
import Switch from "@/components/atoms/Switch";

import { AmountInput } from "@/components/molecules/Wrappers/AmountInput";
import { ErrorText } from "./styles";

function UserDataForm({
  data,
  setData,
  setError,
  setIsFetching,
  retryableError,
  setRetryableError,
}) {
  const { checkDataAndRedirect } = useRedirect();
  const history = useHistory();
  const { id, token } = useParams();
  const { submitData } = useSubmitData(setError, setIsFetching, setData);

  const [documentInstructions, setDocumentInstructions] = useState({
    show: false,
    message: i18n.t("form.documentNumberInfo"),
    type: "info",
  });
  const [emailHasErrors, setEmailHasErrors] = useState(false);
  const [isValidDocument, setIsValidDocument] = useState(
    Boolean(data?.customer?.document_number || data?.document_number),
  );
  const [isValidEmail, setIsValidEmail] = useState(
    Boolean(data?.customer?.email),
  );
  const [acceptedTerms, setAcceptedTerms] = useState(
    Boolean(data?.customer?.terms_accepted_at),
  );

  const initialAcceptedTerms = Boolean(data?.customer?.terms_accepted_at);
  const initialCountryIso =
    data?.customer?.country_iso || data?.customer_country_iso || "";
  const initialDocumentType = (() => {
    const docType =
      data?.customer?.document_type || data?.customer_document_types || "";
    return Array.isArray(docType) ? docType[0] || "" : docType;
  })();
  const initialDocumentNumber =
    data?.customer?.document_number || data?.document_number || "";
  const initialEmail =
    data?.customer?.email || data?.customer_data?.email || "";
  const isDocumentNumberMissingInDeposit =
    Boolean(!initialDocumentNumber) && data?.type === "deposit";
  const isDocumentTypeMissingInDeposit =
    Boolean(!initialDocumentType) && data?.type === "deposit";
  const isEmailMissingInDeposit =
    Boolean(!data?.customer?.email) && data?.type === "deposit";
  const incompleteData =
    isDocumentNumberMissingInDeposit ||
    isDocumentTypeMissingInDeposit ||
    isEmailMissingInDeposit ||
    !initialAcceptedTerms;

  const requestTransaction = async (values) => {
    const dataToSend = { ...data, ...values };
    try {
      setRetryableError(null);
      setIsFetching(true);
      await submitData(dataToSend);
    } catch (error) {
      const originalError = error.message.replace("RETRYABLE_ERROR:", "");
      if (error.message?.startsWith("RETRYABLE_ERROR:")) {
        if (originalError === "invalid document") {
          setRetryableError({
            type: "document",
            title: i18n.t("error.invalidDocumentTitle"),
            message: i18n.t("error.invalidDocumentMessage"),
            action: i18n.t("error.invalidDocumentAction"),
          });
        }
      } else {
        let mappedMessage = originalError;
        if (originalError === "rule-validation-failed: Document is REFUSED") {
          mappedMessage = "rule-validation-failed-document-refused";
        }
        if (
          originalError ===
          "rule-validation-failed: customer-blocked | missing-customer-name | Document is REFUSED"
        ) {
          mappedMessage = "rule-validation-reached-attempts";
        }
        setError(i18n.t([`error.${mappedMessage}`, "error.unspecific"]));
      }
    } finally {
      setIsFetching(false);
    }
  };

  const requestUserData = async (values) => {
    const cleanedDocumentNumber = values.document_number
      ? values.document_number.replace(/\D/g, "")
      : "";
    const shouldUseAcceptTermsAndConfirm =
      data?.type === "deposit" &&
      !initialAcceptedTerms &&
      initialDocumentNumber &&
      initialEmail;

    try {
      setRetryableError(null);
      setIsFetching(true);
      if (shouldUseAcceptTermsAndConfirm) {
        await acceptTermsAndConfirm(id, token, {
          customer_data: { ...values, document_number: cleanedDocumentNumber },
          accepted_terms: values.accepted_terms,
        });
        setData((state) => ({
          ...state,
          customer: {
            ...state.customer,
            terms_accepted_at: new Date().toISOString(),
          },
        }));
        checkDataAndRedirect();
      } else {
        const res = await confirmTransaction(id, token, {
          customer_data: { ...values, document_number: cleanedDocumentNumber },
          accepted_terms: values.accepted_terms,
        });
        setData((state) => ({ ...state, ...res.data }));
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      if (errorMessage === "invalid document") {
        setRetryableError({
          type: "document",
          title: i18n.t("error.invalidDocumentTitle"),
          message: i18n.t("error.invalidDocumentMessage"),
          action: i18n.t("error.invalidDocumentAction"),
        });
      } else {
        let mappedMessage = errorMessage;
        if (errorMessage === "rule-validation-failed: Document is REFUSED") {
          mappedMessage = "rule-validation-failed-document-refused";
        }
        if (
          errorMessage ===
          "rule-validation-failed: customer-blocked | missing-customer-name | Document is REFUSED"
        ) {
          mappedMessage = "rule-validation-reached-attempts";
        }
        setError(i18n.t([`error.${mappedMessage}`, "error.unspecific"]));
      }
    } finally {
      setIsFetching(false);
    }
  };

  const clearRetryableError = () => {
    setRetryableError(null);
  };

  const validateDocumentNumber = (documentNumber) => {
    if (!documentNumber || documentNumber.trim() === "") {
      return {
        isValid: false,
        isEmpty: true,
        message: i18n.t("form.invalidDocument"),
      };
    }
    const cleanedDocument = documentNumber.replace(/\D/g, "");
    return {
      isValid: cleanedDocument.length === 11,
      isEmpty: cleanedDocument.length === 0,
      message:
        cleanedDocument.length === 11
          ? i18n.t("form.documentNumberInfo")
          : i18n.t("form.invalidDocument"),
    };
  };

  const handleDocumentFocus = (setFieldError) => {
    setDocumentInstructions({
      show: true,
      message: i18n.t("form.documentNumberInfo"),
      type: "info",
    });
    setFieldError("document_number", "");
  };

  const handleDocumentBlur = (
    documentNumber,
    setFieldTouched,
    setFieldError,
  ) => {
    setFieldTouched("document_number");
    setFieldError("document_number", "");
    const validation = validateDocumentNumber(documentNumber);
    if (validation.isEmpty) {
      setDocumentInstructions({
        show: true,
        message: validation.message,
        type: "error",
      });
      setFieldError("document_number", validation.message);
    } else if (validation.isValid) {
      setDocumentInstructions({
        show: true,
        message: validation.message,
        type: "info",
      });
    } else {
      setDocumentInstructions({
        show: true,
        message: validation.message,
        type: "error",
      });
      setFieldError("document_number", validation.message);
    }
  };

  const handleEmailFocus = (setFieldError) => {
    setEmailHasErrors(false);
    setFieldError("email", "");
  };

  const validEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailBlur = (emailAddress, setFieldTouched, setFieldError) => {
    setFieldTouched("email");
    setFieldError("email", "");
    if (validEmail(emailAddress)) {
      setEmailHasErrors(false);
    } else {
      setFieldError("email", "error");
      setEmailHasErrors(true);
    }
  };

  return (
    <Formik
      validationSchema={conditionalUserSchema}
      initialValues={{
        accepted_terms: acceptedTerms,
        merchant_request_amount: data?.merchant_request_amount || "",
        bank_payment_data: data?.bank_payment_data || "",
        document_number: initialDocumentNumber,
        document_type: initialDocumentType,
        country_iso: initialCountryIso,
        email: initialEmail,
      }}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={incompleteData ? requestUserData : requestTransaction}
      context={{ incompleteData }}
    >
      {({
        errors,
        setErrors,
        setFieldTouched,
        setFieldError,
        setFieldValue,
        values,
      }) =>
        retryableError ? (
          <div className="size-full flex grow flex-col justify-around items-center">
            <InvalidDocument />
            <div className="flex flex-col gap-6 px-6">
              <p className="text-xl font-bold">{retryableError.title}</p>
              <p className="text-lg font-bold">{retryableError.message}</p>
              <p className="text-xs">{retryableError.action}</p>
            </div>
            <Button
              type="button"
              handleClick={() => {
                clearRetryableError();
              }}
            >
              {i18n.t("error.tryAgainButton") || "Volver a intentar"}
            </Button>
          </div>
        ) : (
          <Form onChange={() => setErrors({})} className="w-full">
            {data?.merchant_request_amount && (
              <AmountInput
                value={data?.merchant_request_amount}
                currency={String(data?.merchant_request_currency_iso || "")}
              />
            )}
            <div className="mb-5 mx-auto text-black text-lg max-w-xs font-semibold">
              {i18n.t([`checkout.${data?.type}.formTitle`, ""])}
            </div>
            <div className="flex flex-col justify-between items-center">
              {!data?.merchant_request_amount && (
                <Input
                  id="merchant_request_amount"
                  type="text"
                  label={i18n.t("form.amountLabel.default")}
                  placeholder={i18n.t("form.amountPlaceholder")}
                  value={values?.merchant_request_amount || ""}
                  error={!!errors?.merchant_request_amount}
                  disabled={!!values?.merchant_request_amount}
                  errorText={errors?.merchant_request_amount}
                />
              )}
              {initialDocumentNumber && data?.type === "deposit" ? (
                <FakeInput
                  label={i18n.t(
                    `form.document.${values?.document_type || "generic"}`,
                  )}
                  value={formatDocumentMask(
                    values?.document_number,
                    values?.document_type,
                  )}
                />
              ) : (
                <div className="w-full mb-5">
                  <Input
                    id="document_number"
                    name="document_number"
                    type="text"
                    label={i18n.t(
                      `form.document.${values?.document_type || "generic"}`,
                    )}
                    placeholder={i18n.t("form.documentPlaceholder")}
                    value={values?.document_number || ""}
                    error={!!errors?.document_number}
                    handleFocus={() => handleDocumentFocus(setFieldError)}
                    handleBlur={() =>
                      handleDocumentBlur(
                        values?.document_number,
                        setFieldTouched,
                        setFieldError,
                      )
                    }
                    handleChange={({ target }) => {
                      setFieldTouched("document_number", true);
                      setIsValidDocument(
                        target.value.replace(/-/g, "").length === 11,
                      );
                      const documentType = values?.document_type || "cuit-cuil";
                      const type = Array.isArray(documentType)
                        ? documentType[0] || "cuit-cuil"
                        : documentType;
                      setFieldValue(
                        "document_number",
                        formatDocumentMask(
                          target.value,
                          String(type).toLowerCase(),
                        ),
                      );
                    }}
                    maxLength={14}
                    disabled={!!initialDocumentNumber}
                  />
                  {documentInstructions.show && (
                    <div
                      className={`mt-1 text-[0.65rem] pl-1 text-left ${
                        documentInstructions.type === "error"
                          ? "text-cripten-red"
                          : "text-dark-gray"
                      }`}
                    >
                      {documentInstructions.message}
                    </div>
                  )}
                </div>
              )}
              {isEmailMissingInDeposit ? (
                <div className="w-full mb-5">
                  <Input
                    id="email"
                    name="email"
                    type="text"
                    label={i18n.t("form.email")}
                    placeholder={i18n.t("form.emailPlaceholder")}
                    value={values?.email || ""}
                    error={!!errors?.email}
                    handleFocus={() => handleEmailFocus(setFieldError)}
                    handleChange={({ target }) => {
                      setFieldValue("email", target.value);
                      setIsValidEmail(validEmail(target.value));
                    }}
                    handleBlur={() =>
                      handleEmailBlur(
                        values?.email,
                        setFieldTouched,
                        setFieldError,
                      )
                    }
                    disabled={Boolean(data?.customer?.email)}
                  />
                  {emailHasErrors && (
                    <div className="mt-1 text-[0.65rem] text-cripten-red pl-1 text-left">
                      {i18n.t("form.invalidEmail")}
                    </div>
                  )}
                </div>
              ) : (
                <FakeInput label={i18n.t("form.email")} value={values?.email} />
              )}
            </div>
            {!initialAcceptedTerms && (
              <div
                className="flex mt-5 items-center gap-3 bg-white"
                data-theme="systemSwitchTheme"
              >
                <Switch
                  isChecked={values?.accepted_terms}
                  onChange={(e) => {
                    setFieldTouched("accepted_terms");
                    setFieldValue("accepted_terms", !values.accepted_terms);
                    setAcceptedTerms(e.target.checked);
                  }}
                />
                <p className="m-0 text-left text-sm text-black">
                  <Trans
                    i18nKey="form.terms"
                    i18n={i18n}
                    components={[
                      <Link
                        className="text-blue-600 hover:text-blue-800 font-bold underline"
                        to={{
                          pathname: `/terms-and-conditions/${id}/${token}`,
                          search: history.location.search,
                        }}
                      />,
                    ]}
                  />
                </p>
                {errors?.accepted_terms && (
                  <ErrorText>{errors?.accepted_terms}</ErrorText>
                )}
              </div>
            )}
            <Button
              type="submit"
              disabled={!acceptedTerms || !isValidDocument || !isValidEmail}
            >
              {!incompleteData
                ? i18n.t([`form.submit.${data?.type}`, "form.submit.default"])
                : i18n.t("form.submit.next")}
            </Button>
          </Form>
        )
      }
    </Formik>
  );
}

UserDataForm.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.number,
    merchant_request_amount: PropTypes.number,
    customer_country_iso: PropTypes.string,
    customer_document_types: PropTypes.arrayOf(PropTypes.string),
    document_number: PropTypes.string,
    bank_payment_data: PropTypes.shape({
      qr_code: PropTypes.string,
      account_holder: PropTypes.string,
      account_id: PropTypes.string,
      bank_name: PropTypes.string,
    }),
    merchant_request_currency_iso: PropTypes.string,
    redirect_url_failed: PropTypes.string,
    type: PropTypes.string,
    customer: PropTypes.shape({
      birth_date: PropTypes.string,
      document_number: PropTypes.string,
      document_type: PropTypes.string,
      country_iso: PropTypes.string,
      email: PropTypes.string,
      name: PropTypes.string,
      terms_accepted_at: PropTypes.string,
    }),
    customer_data: PropTypes.shape({
      birth_date: PropTypes.string,
      document_number: PropTypes.string,
      email: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  setData: PropTypes.func,
  setError: PropTypes.func,
  setIsFetching: PropTypes.func,

  retryableError: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
    action: PropTypes.string,
  }),
  setRetryableError: PropTypes.func,
};

export default UserDataForm;
